/**
 * Turns the error thrown by ApiService.downloadFileBuffer into the sentence the ops user should
 * see. The download request uses responseType 'arraybuffer', so an error body arrives as an
 * ArrayBuffer that has to be decoded before the server's message can be read.
 *
 * Returns null when the request was cancelled on purpose (the modal was closed), so callers can
 * stay silent instead of reporting an error nobody caused.
 */
import { getErrorMessage } from './errorMessage';

export type ExportError = {
  code?: string;
  message?: string;
  response?: {
    status?: number;
    headers?: Record<string, unknown>;
    data?: unknown;
  };
};

export const EXPORT_INTERRUPTED_MESSAGE =
  'The download was interrupted before the file was complete. Please try again.';
export const EXPORT_TIMEOUT_MESSAGE =
  'The download took too long and was stopped. Try a shorter date range or fewer lounges.';
/** The single-lounge report has no lounge count to reduce. */
export const EXPORT_TIMEOUT_MESSAGE_SINGLE_LOUNGE =
  'The download took too long and was stopped. Try a shorter date range.';
export const EXPORT_FAILED_MESSAGE =
  'The report could not be downloaded. Please try again, and contact support if it keeps failing.';

export type ExportErrorOptions = {
  /** The report covers one lounge, so advice about picking fewer lounges is left out. */
  singleLounge?: boolean;
};

// Realm-safe: a buffer created in another JS realm (a worker, or Node in tests) fails instanceof.
const isArrayBuffer = (data: unknown): data is ArrayBuffer =>
  data instanceof ArrayBuffer ||
  Object.prototype.toString.call(data) === '[object ArrayBuffer]';

const decodeBody = (data: unknown): Record<string, unknown> | null => {
  try {
    if (isArrayBuffer(data)) {
      return JSON.parse(new TextDecoder().decode(data));
    }
    if (typeof data === 'string') return JSON.parse(data);
    if (data && typeof data === 'object')
      return data as Record<string, unknown>;
  } catch {
    // not JSON (an HTML error page, for instance): fall through to the status-based text
  }
  return null;
};

export const isCancelledDownload = (err: unknown): boolean =>
  (err as ExportError | null)?.code === 'ERR_CANCELED';

export function getExportErrorMessage(
  err: unknown,
  { singleLounge = false }: ExportErrorOptions = {},
): string | null {
  const error = (err ?? {}) as ExportError;
  if (isCancelledDownload(error)) return null;
  if (!error.response) {
    // No HTTP response at all: the connection was cut (a stream that stopped mid-file) or the
    // client gave up waiting. Neither is fixed by picking other dates.
    if (error.code === 'ECONNABORTED') {
      return singleLounge
        ? EXPORT_TIMEOUT_MESSAGE_SINGLE_LOUNGE
        : EXPORT_TIMEOUT_MESSAGE;
    }
    return EXPORT_INTERRUPTED_MESSAGE;
  }
  // Decoded here because the body arrived as bytes; the shared helper then reads the message
  // out of it the same way as everywhere else in the portal (message, error.message, errors[]).
  const message = getErrorMessage(
    { response: { data: decodeBody(error.response.data) } },
    EXPORT_FAILED_MESSAGE,
  );
  if (error.response.status === 429) {
    const header = error.response.headers?.['retry-after'];
    const seconds = Number.parseInt(String(header ?? ''), 10);
    if (Number.isFinite(seconds) && seconds > 0) {
      return `${message} Try again in ${seconds} seconds.`;
    }
  }
  return message;
}
